import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import unitService from '../../../services/unitService';
import {
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    IconButton,
    Chip,
    LinearProgress,
} from '@mui/material';
import {
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Schedule as ScheduleIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    FileDownload as FileDownloadIcon,
    Notifications as NotificationsIcon,
    Refresh as RefreshIcon,
    FilterList as FilterListIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import {
    fetchDashboard,
    fetchQueryHistory,
    setTimeRange,
    setDateRange,
    setUnitFilter,
    setDeviceTypeFilter,
    setStatusFilter,
    setQueryTypeFilter,
    setResponseTimeRange,
    setPage,
    setPageSize,
    clearFilters,
    exportReport,
    selectStatistics,
    selectVolumeData,
    selectTypeDistribution,
    selectSystemPerformance,
    selectSystemHealthStatus,
    selectQueryHistory,
    selectQueryHistoryMeta,
    selectFilters,
    selectDashboardLoading,
    selectHistoryLoading,
} from './sliceTab5';

function Tab5QueryMonitoring() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Redux state
    const statistics = useSelector(selectStatistics);
    const volumeData = useSelector(selectVolumeData);
    const typeDistribution = useSelector(selectTypeDistribution);
    const systemPerformance = useSelector(selectSystemPerformance);
    const systemHealthStatus = useSelector(selectSystemHealthStatus);
    const queryHistory = useSelector(selectQueryHistory);
    const historyMeta = useSelector(selectQueryHistoryMeta);
    const filters = useSelector(selectFilters);
    const dashboardLoading = useSelector(selectDashboardLoading);
    const historyLoading = useSelector(selectHistoryLoading);

    // Local state for filters UI
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [minResponseTime, setMinResponseTime] = useState('');
    const [maxResponseTime, setMaxResponseTime] = useState('');
    const [units, setUnits] = useState([]);

    // Fetch units for the dropdown using init endpoint
    useEffect(() => {
        const fetchUnits = async () => {
            try {
                const response = await unitService.getUnitInit();
                setUnits(response.units || []);
            } catch (error) {
                console.error('Error fetching units:', error);
            }
        };
        fetchUnits();
    }, []);

    // Fetch data on mount and when filters change
    useEffect(() => {
        dispatch(fetchDashboard());
        dispatch(fetchQueryHistory());
    }, [dispatch, filters.timeRange, filters.unitId, filters.deviceType, filters.status, filters.queryType, filters.page, filters.pageSize]);

    // Handle time range change
    const handleTimeRangeChange = (range) => {
        dispatch(setTimeRange(range));
    };

    // Handle filter changes
    const handleUnitChange = (e) => {
        const value = e.target.value;
        dispatch(setUnitFilter(value === '' ? null : parseInt(value, 10)));
    };

    const handleDeviceTypeChange = (e) => {
        dispatch(setDeviceTypeFilter(e.target.value || null));
    };

    const handleStatusChange = (e) => {
        dispatch(setStatusFilter(e.target.value || null));
    };

    const handleQueryTypeChange = (e) => {
        dispatch(setQueryTypeFilter(e.target.value || null));
    };

    const handleApplyResponseTimeFilter = () => {
        dispatch(
            setResponseTimeRange({
                min: minResponseTime ? parseInt(minResponseTime) : null,
                max: maxResponseTime ? parseInt(maxResponseTime) : null,
            })
        );
    };

    const handleClearFilters = () => {
        dispatch(clearFilters());
        setMinResponseTime('');
        setMaxResponseTime('');
    };

    const handleRefresh = () => {
        dispatch(fetchDashboard());
        dispatch(fetchQueryHistory());
    };

    const handleExportMonthly = () => {
        const now = new Date();
        dispatch(
            exportReport({
                type: 'monthly',
                year: now.getFullYear(),
                month: now.getMonth() + 1,
                format: 'excel',
            })
        );
    };

    const handleExportQuarterly = () => {
        const now = new Date();
        const quarter = Math.floor(now.getMonth() / 3) + 1;
        dispatch(
            exportReport({
                type: 'quarterly',
                year: now.getFullYear(),
                quarter: quarter,
                format: 'excel',
            })
        );
    };

    const handlePageChange = (event, newPage) => {
        dispatch(setPage(newPage + 1));
    };

    const handlePageSizeChange = (event) => {
        dispatch(setPageSize(parseInt(event.target.value, 10)));
    };

    // Format number with commas
    const formatNumber = (num) => {
        return num?.toLocaleString() || '0';
    };

    // Format trend
    const formatTrend = (trend) => {
        if (!trend || trend === 0) return null;
        const Icon = trend > 0 ? TrendingUpIcon : TrendingDownIcon;
        const color = trend > 0 ? 'success' : 'error';
        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1, color: `${color}.main` }}>
                <Icon sx={{ fontSize: 16, mr: 0.5 }} />
                {Math.abs(trend)}%
            </Box>
        );
    };

    // Pie chart colors
    const COLORS = ['#4caf50', '#9c27b0', '#2196f3', '#ff9800'];

    return (
        <Box
            sx={{
                p: 3,
                height: '100%',
                overflow: 'auto',
                pb: 2,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    p: 2,
                }}
            >
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {t('queryMonitoring.title')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('queryMonitoring.subtitle')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                        icon={<CheckCircleIcon />}
                        label={t('queryMonitoring.realtimeActive')}
                        color="success"
                        sx={{
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            px: 2,
                            py: 1,
                        }}
                    />
                    <IconButton
                        onClick={handleRefresh}
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.dark' },
                            boxShadow: 2,
                        }}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Time Range Selector */}
            <Card
                sx={{
                    mb: 3,
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ScheduleIcon sx={{ mr: 1 }} />
                        <Typography variant="subtitle1">{t('queryMonitoring.selectTimeRange')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {['Last24Hours', 'Last7Days', 'Last30Days', 'ThisQuarter', 'ThisYear', 'CustomRange'].map((range) => (
                            <Button
                                key={range}
                                variant={filters.timeRange === range ? 'contained' : 'outlined'}
                                onClick={() => handleTimeRangeChange(range)}
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: filters.timeRange === range ? 3 : 1,
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                {t(`queryMonitoring.timeRange.${range}`)}
                            </Button>
                        ))}
                    </Box>
                </CardContent>
            </Card>

            {/* Statistics Cards */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(4, 1fr)',
                    },
                    gap: 3,
                    width: '100%',
                    mb: 3,
                }}
            >
                <Card
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <SearchIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                                {formatNumber(statistics.totalQueries)}
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                                {t('queryMonitoring.totalQueries')}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8, textAlign: 'center' }}>
                                {statistics.periodLabel}
                                {formatTrend(statistics.totalQueriesTrend)}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        background: 'linear-gradient(135deg, #2d8659 0%, #17a2b8 100%)',
                        color: 'white',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <CheckCircleIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                                {formatNumber(statistics.successfulQueries)}
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                                {t('queryMonitoring.successfulQueries')}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8, textAlign: 'center' }}>
                                {statistics.successRate}% {t('queryMonitoring.successRate')}
                                {formatTrend(statistics.successRateTrend)}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <ErrorIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                                {formatNumber(statistics.failedQueries)}
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                                {t('queryMonitoring.failedQueries')}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8, textAlign: 'center' }}>
                                {statistics.failureRate}% {t('queryMonitoring.failureRate')}
                                {formatTrend(-statistics.failureRate)}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Card
                    sx={{
                        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        color: 'white',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <ScheduleIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                                {statistics.averageResponseTime}s
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
                                {t('queryMonitoring.avgResponseTime')}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8, textAlign: 'center' }}>
                                {t('queryMonitoring.monthlyAverage')}
                                {formatTrend(statistics.responseTimeTrend)}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Charts Section */}
            <Card
                sx={{
                    mb: 3,
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('queryMonitoring.realtimePerformance')}
                    </Typography>

                    <Grid container spacing={3}>
                        {/* Volume Chart */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('queryMonitoring.queryVolume')}
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={volumeData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="timeLabel" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="queryCount" fill="#2196f3" name={t('queryMonitoring.queries')} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Grid>

                        {/* System Performance and Type Distribution */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('queryMonitoring.systemPerformance')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                                <ResponsiveContainer width="100%" height={200}>
                                    <PieChart>
                                        <Pie
                                            data={[
                                                { name: 'Success', value: systemPerformance },
                                                { name: 'Failed', value: 100 - systemPerformance },
                                            ]}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={80}
                                            fill="#4caf50"
                                            dataKey="value"
                                        >
                                            <Cell fill="#4caf50" />
                                            <Cell fill="#ff9800" />
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <Typography variant="h4" color="success.main" sx={{ mt: -10 }}>
                                    {systemPerformance}%
                                </Typography>
                                <Typography variant="subtitle2" sx={{ mt: 8 }}>
                                    {systemHealthStatus}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t('queryMonitoring.overallHealth')}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('queryMonitoring.queryTypeDistribution')}
                            </Typography>
                            <Box sx={{ py: 2 }}>
                                {typeDistribution.map((item, index) => (
                                    <Box key={item.queryType} sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Chip
                                                label={item.queryType.replace(/_/g, ' ')}
                                                size="small"
                                                sx={{ backgroundColor: COLORS[index % COLORS.length] }}
                                            />
                                            <Typography variant="body2">{item.percentage}%</Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={item.percentage}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                backgroundColor: 'grey.200',
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: COLORS[index % COLORS.length],
                                                },
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Advanced Filters */}
            <Card
                sx={{
                    mb: 3,
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                            p: 2,
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FilterListIcon sx={{ mr: 1 }} />
                            <Typography variant="subtitle1">{t('queryMonitoring.advancedFilters')}</Typography>
                        </Box>
                        <Button
                            variant="contained"
                            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                py: 1,
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: 2,
                                '&:hover': {
                                    boxShadow: 4,
                                    transform: 'translateY(-1px)',
                                },
                            }}
                        >
                            {showAdvancedFilters ? t('common.hide') : t('common.show')}
                        </Button>
                    </Box>

                    {showAdvancedFilters && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small" variant="outlined">
                                    <InputLabel>{t('queryMonitoring.filterByUnit')}</InputLabel>
                                    <Select 
                                        value={filters.unitId || ''} 
                                        onChange={handleUnitChange} 
                                        label={t('queryMonitoring.filterByUnit')}
                                        sx={{ width: '100%', minWidth: '200px' }}
                                    >
                                        <MenuItem value="">{t('common.allUnits')}</MenuItem>
                                        {units.map((unit) => (
                                            <MenuItem key={unit.id} value={unit.id}>
                                                {unit.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small" variant="outlined">
                                    <InputLabel>{t('queryMonitoring.filterByDevice')}</InputLabel>
                                    <Select
                                        value={filters.deviceType || ''}
                                        onChange={handleDeviceTypeChange}
                                        label={t('queryMonitoring.filterByDevice')}
                                        sx={{ width: '100%', minWidth: '200px' }}
                                    >
                                        <MenuItem value="">{t('common.allDevices')}</MenuItem>
                                        <MenuItem value="Mobile App">Mobile App</MenuItem>
                                        <MenuItem value="Web Portal">Web Portal</MenuItem>
                                        <MenuItem value="Kiosk Terminal">Kiosk Terminal</MenuItem>
                                        <MenuItem value="Desktop Application">Desktop Application</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small" variant="outlined">
                                    <InputLabel>{t('queryMonitoring.queryStatus')}</InputLabel>
                                    <Select 
                                        value={filters.status || ''} 
                                        onChange={handleStatusChange} 
                                        label={t('queryMonitoring.queryStatus')}
                                        sx={{ width: '100%', minWidth: '200px' }}
                                    >
                                        <MenuItem value="">{t('common.allStatus')}</MenuItem>
                                        <MenuItem value="Success">Success</MenuItem>
                                        <MenuItem value="Failed">Failed</MenuItem>
                                        <MenuItem value="Timeout">Timeout</MenuItem>
                                        <MenuItem value="Error">Error</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small" variant="outlined">
                                    <InputLabel>{t('queryMonitoring.queryType')}</InputLabel>
                                    <Select 
                                        value={filters.queryType || ''} 
                                        onChange={handleQueryTypeChange} 
                                        label={t('queryMonitoring.queryType')}
                                        sx={{ width: '100%', minWidth: '200px' }}
                                    >
                                        <MenuItem value="">{t('common.allTypes')}</MenuItem>
                                        <MenuItem value="CCCD_Verification">CCCD Verification</MenuItem>
                                        <MenuItem value="VNeID_Authentication">VNeID Authentication</MenuItem>
                                        <MenuItem value="Biometric_Match">Biometric Match</MenuItem>
                                        <MenuItem value="Face_Recognition">Face Recognition</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    label={t('queryMonitoring.minResponseTime')}
                                    placeholder={t('queryMonitoring.minResponseTime')}
                                    value={minResponseTime}
                                    onChange={(e) => setMinResponseTime(e.target.value)}
                                    type="number"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    label={t('queryMonitoring.maxResponseTime')}
                                    placeholder={t('queryMonitoring.maxResponseTime')}
                                    value={maxResponseTime}
                                    onChange={(e) => setMaxResponseTime(e.target.value)}
                                    type="number"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <Button 
                                    fullWidth 
                                    variant="contained" 
                                    onClick={handleApplyResponseTimeFilter} 
                                    startIcon={<SearchIcon />}
                                    sx={{ width: '100%' }}
                                >
                                    {t('common.applyFilters')}
                                </Button>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <Button 
                                    fullWidth 
                                    variant="outlined" 
                                    onClick={handleClearFilters} 
                                    startIcon={<ClearIcon />}
                                    sx={{ width: '100%' }}
                                >
                                    {t('common.clear')}
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                </CardContent>
            </Card>

            {/* Query History Table */}
            <Card>
                <CardContent sx={{ p: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                            p: 3,
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="h6">{t('queryMonitoring.detailedHistory')}</Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<FileDownloadIcon />}
                                onClick={handleExportMonthly}
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: 2,
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                {t('queryMonitoring.exportMonthly')}
                            </Button>
                            <Button
                                variant="contained"
                                color="info"
                                startIcon={<FileDownloadIcon />}
                                onClick={handleExportQuarterly}
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: 2,
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                {t('queryMonitoring.exportQuarterly')}
                            </Button>
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<NotificationsIcon />}
                                onClick={() => navigate('/system/alert-configurations')}
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: 2,
                                    '&:hover': {
                                        boxShadow: 4,
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                {t('queryMonitoring.configureAlerts')}
                            </Button>
                        </Box>
                    </Box>

                    {historyLoading ? (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                {t('common.loading')}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('queryMonitoring.date')}</TableCell>
                                            <TableCell>{t('queryMonitoring.unit')}</TableCell>
                                            <TableCell>{t('queryMonitoring.device')}</TableCell>
                                            <TableCell align="right">{t('queryMonitoring.totalQueries')}</TableCell>
                                            <TableCell align="right">{t('queryMonitoring.successRate')}</TableCell>
                                            <TableCell align="right">{t('queryMonitoring.avgResponseTime')}</TableCell>
                                            <TableCell>{t('queryMonitoring.peakHour')}</TableCell>
                                            <TableCell align="center">{t('queryMonitoring.violations')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {queryHistory.map((row) => (
                                            <TableRow key={`${row.date}-${row.unitId}`}>
                                                <TableCell>{new Date(row.date).toLocaleDateString()}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Box
                                                            sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: '50%',
                                                                backgroundColor:
                                                                    row.connectionStatus === 'Active'
                                                                        ? 'success.main'
                                                                        : row.connectionStatus === 'Connection Lost'
                                                                        ? 'error.main'
                                                                        : 'warning.main',
                                                                mr: 1,
                                                            }}
                                                        />
                                                        {row.unitName}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {row.deviceType || 'N/A'} ({row.deviceCount})
                                                </TableCell>
                                                <TableCell align="right">{formatNumber(row.totalQueries)}</TableCell>
                                                <TableCell align="right">
                                                    <Chip
                                                        label={`${row.successRate}%`}
                                                        color={row.successRate >= 95 ? 'success' : row.successRate >= 90 ? 'warning' : 'error'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">{row.averageResponseTime}s</TableCell>
                                                <TableCell>{row.peakHour || 'N/A'}</TableCell>
                                                <TableCell align="center">
                                                    {row.violations > 0 ? (
                                                        <Chip label={row.violations} color="error" size="small" />
                                                    ) : (
                                                        <Chip label="0" color="success" size="small" />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={historyMeta.total}
                                rowsPerPage={historyMeta.pageSize}
                                page={historyMeta.page - 1}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handlePageSizeChange}
                            />
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

export default Tab5QueryMonitoring;
